import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import type { GuidedEditPayload } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CreateCampaignModal";
import type {
  FacebookPage,
  InstagramAccount,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import CreativeBlockEditor from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CreativeBlockEditor";
import type { CreativeBlock } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/guidedFlow/types";
import { useEffect, useMemo, useRef, useState } from "react";

type GuidedCreative = GuidedEditPayload["creatives"][number];

type EditAdCreativeModalProps = {
  isOpen: boolean;
  isSaving?: boolean;
  creative: GuidedCreative;
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  createUploadUrl: (
    file: File,
    callback: (params: {
      signedUrl?: string;
      fileKey?: string;
      file_key?: string;
      cdn_url?: string;
    }) => void
  ) => void;
  onClose: () => void;
  onSave: (creative: GuidedCreative) => void | Promise<void>;
  onFacebookPageChange?: (facebookPageId: string) => void;
};

export default function EditAdCreativeModal({
  isOpen,
  isSaving = false,
  creative,
  pages,
  instagramAccounts,
  createUploadUrl,
  onClose,
  onSave,
  onFacebookPageChange,
}: EditAdCreativeModalProps) {
  const [value, setValue] = useState<CreativeBlock>({
    primaryText: "",
    fbPage: null,
    instagramAccount: null,
  });
  const [editingCreativeName, setEditingCreativeName] = useState<{
    index: number;
    value: string;
  } | null>(null);
  const uploadRefs = useRef<
    Record<string, { fileKey?: string; cdn_url?: string }>
  >({});

  useEffect(() => {
    if (!isOpen) return;
    const mappedPage =
      pages.find((page) => page.id === creative.fb_page_id) ?? null;
    const mappedInstagram =
      instagramAccounts.find(
        (account) => account.id === creative.instagram_id
      ) ?? null;
    setValue({
      primaryText: creative.primary_text ?? "",
      squareMediaUrl: creative.square_url,
      squareMediaFileKey: creative.square_file_key,
      squareMediaType: creative.square_media_type,
      verticalMediaUrl: creative.portrait_url,
      verticalMediaFileKey: creative.portrait_file_key,
      verticalMediaType: creative.portrait_media_type,
      fbPage: mappedPage,
      instagramAccount: mappedInstagram,
    });
    setEditingCreativeName(null);
    uploadRefs.current = {};
  }, [creative, instagramAccounts, isOpen, pages]);

  const pageOptions = useMemo(
    () => pages.map((page) => ({ label: page.name, value: page })),
    [pages]
  );
  const instagramOptions = useMemo(
    () =>
      instagramAccounts.map((instagramAccount) => ({
        label: instagramAccount.username || instagramAccount.name || "",
        value: instagramAccount,
      })),
    [instagramAccounts]
  );

  const createUploadUrlForSlot =
    (slotKey: string) =>
    ({
      file,
      callback,
    }: {
      file: File;
      callback: (r: { signedUrl: string }) => void | Promise<string | void>;
    }) =>
      new Promise<{ signedUrl: string; fileKey: string }>((resolve) => {
        createUploadUrl(file, (res: any) => {
          const fileKey = res?.fileKey ?? res?.file_key ?? "";
          const signedUrl = res?.signedUrl ?? res?.signed_url ?? "";
          const cdn_url = res?.cdn_url ?? "";
          uploadRefs.current[slotKey] = { fileKey, cdn_url };
          callback({ signedUrl });
          resolve({ signedUrl, fileKey });
        });
      });

  const selectContainerStyles = {
    container: () => ({
      position: "relative" as const,
      width: "100%",
      backgroundColor: "white",
      zIndex: 2,
      cursor: "pointer",
    }),
    dropdownIndicator: (provided: Record<string, unknown>) => ({
      ...provided,
      backgroundColor: "white",
      color: "gray.500",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
  };

  const toGuidedCreative = (): GuidedCreative => ({
    primary_text: value.primaryText,
    ...(value.squareMediaUrl ? { square_url: value.squareMediaUrl } : {}),
    ...(value.squareMediaFileKey
      ? { square_file_key: value.squareMediaFileKey }
      : {}),
    ...(value.squareMediaType
      ? { square_media_type: value.squareMediaType }
      : {}),
    ...(value.verticalMediaUrl ? { portrait_url: value.verticalMediaUrl } : {}),
    ...(value.verticalMediaFileKey
      ? { portrait_file_key: value.verticalMediaFileKey }
      : {}),
    ...(value.verticalMediaType
      ? { portrait_media_type: value.verticalMediaType }
      : {}),
    fb_page_id: value.fbPage?.id ?? creative.fb_page_id ?? "",
    instagram_id: value.instagramAccount?.id ?? creative.instagram_id ?? "",
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Ad Creative</ModalHeader>
        <ModalBody>
          <CreativeBlockEditor
            creative={value}
            index={0}
            totalCreatives={1}
            pageOptions={pageOptions}
            instagramOptions={instagramOptions}
            createUploadUrlForSlot={createUploadUrlForSlot}
            uploadRefs={uploadRefs}
            onUpdate={(_, patch) =>
              setValue((prev) => ({
                ...prev,
                ...patch,
              }))
            }
            onRemove={() => {}}
            onAddMore={() => {}}
            onFacebookPageChange={onFacebookPageChange}
            editingName={editingCreativeName}
            onEditingNameChange={setEditingCreativeName}
            allowCreativeCountChanges={false}
            selectContainerStyles={selectContainerStyles}
          />
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button
              bg="gray.900"
              color="white"
              _hover={{ bg: "gray.800" }}
              onClick={() => onSave(toGuidedCreative())}
              isLoading={isSaving}
            >
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
